import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { inventoryApi } from '../api/inventory-api-instance';
import { ErrorState } from '../components/ErrorState';
import { PageControls } from '../components/PageControls';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import type { Site, Unit } from '../domain/inventory';
import { siteContactSearchText } from '../domain/siteContact';
import {
  FULL_FLEET_ITEMS_QUERY_KEY,
  FULL_FLEET_PAGE_SIZE,
} from '../features/dashboard/aggregations';
import { operationallyVisibleItems } from '../features/inventory/operational-visibility';
import { useOpenRequestCountsBySite } from '../features/field-work/open-requests-by-site';
import { SiteContactSummary } from '../features/site-contact/SiteContactSummary';
import { toMap } from '../lib/collections';
import { siteLabel } from '../lib/site-label';

const CUSTOMER_PAGE_SIZE = 10;

function countBy(values: string[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  return counts;
}

function groupUnits(units: Unit[]): Map<string, Unit[]> {
  const grouped = new Map<string, Unit[]>();
  for (const unit of units) grouped.set(unit.siteId, [...(grouped.get(unit.siteId) ?? []), unit]);
  return grouped;
}

function hasCoordinates(site: Site): boolean {
  return Number.isFinite(site.latitude) && Number.isFinite(site.longitude);
}

function formatUnitStatus(status: string | undefined): string {
  if (!status) return 'Status not recorded';
  return status
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function SitesPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const requestedPage = Math.max(1, Number(params.get('page') ?? 1) || 1);
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => inventoryApi.listCustomers(),
  });
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => inventoryApi.listSites() });
  // Leatherback lists units one customer at a time.
  const customerIds = (customers.data ?? []).map((customer) => customer.id);
  const units = useQuery({
    queryKey: ['units', customerIds],
    queryFn: async () =>
      (await Promise.all(customerIds.map((id) => inventoryApi.listUnits(id)))).flat(),
    enabled: customers.isSuccess,
  });
  const items = useQuery({
    queryKey: FULL_FLEET_ITEMS_QUERY_KEY,
    queryFn: () => inventoryApi.listItems({ pageSize: FULL_FLEET_PAGE_SIZE }),
  });

  const visibleItems = useMemo(
    () => operationallyVisibleItems(items.data?.data ?? [], toMap(sites.data)),
    [items.data, sites.data],
  );
  const assignedCounts = useMemo(
    () => countBy(visibleItems.map((item) => item.siteId)),
    [visibleItems],
  );
  const locatedCounts = useMemo(
    () => countBy(visibleItems.map((item) => item.locationSiteId ?? item.siteId)),
    [visibleItems],
  );
  const unitsBySite = useMemo(() => groupUnits(units.data ?? []), [units.data]);
  const openRequestsBySite = useOpenRequestCountsBySite();

  const groupedCustomers = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (customers.data ?? [])
      .map((customer) => {
        const customerMatches = `${customer.id} ${customer.name} ${customer.displayName ?? ''}`
          .toLowerCase()
          .includes(needle);
        const customerSites = (sites.data ?? []).filter((site) => {
          if (site.customerId !== customer.id) return false;
          if (!needle || customerMatches) return true;
          return `${site.id} ${site.name} ${site.displayName ?? ''} ${site.region ?? ''} ${site.officialSiteId ?? ''} ${siteContactSearchText(site)}`
            .toLowerCase()
            .includes(needle);
        });
        return { customer, sites: customerSites };
      })
      .filter((group) => group.sites.length > 0);
  }, [customers.data, sites.data, query]);
  const pageCount = Math.max(1, Math.ceil(groupedCustomers.length / CUSTOMER_PAGE_SIZE));
  const page = Math.min(requestedPage, pageCount);
  const visibleCustomers = groupedCustomers.slice(
    (page - 1) * CUSTOMER_PAGE_SIZE,
    page * CUSTOMER_PAGE_SIZE,
  );

  function setPage(nextPage: number): void {
    const next = new URLSearchParams(params);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setParams(next);
  }

  const loading = customers.isLoading || sites.isLoading || units.isLoading || items.isLoading;
  // Units only add detail to each site, so failing to load them leaves the
  // hierarchy usable (and its contact/address editing reachable).
  const failed = customers.isError || sites.isError || items.isError;
  if (loading) return <PageLoadingState label="Loading customers, sites, and units…" />;
  if (failed) {
    return (
      <ErrorState
        title="Customer and site hierarchy could not be loaded"
        rpcName="ListSites"
        onRetry={() => {
          void customers.refetch();
          void sites.refetch();
          void units.refetch();
          void items.refetch();
        }}
      />
    );
  }
  const unitsUnavailable = units.isError;

  const coordinateCount = (sites.data ?? []).filter(hasCoordinates).length;

  return (
    <>
      <PageHeader title="Customers and sites" />

      <section className="compact-metrics" aria-label="Customer and site summary">
        <div>
          <span>Customers</span>
          <strong>{(customers.data ?? []).length.toLocaleString()}</strong>
        </div>
        <div>
          <span>Sites</span>
          <strong>{(sites.data ?? []).length.toLocaleString()}</strong>
        </div>
        <div>
          <span>Units</span>
          <strong>{unitsUnavailable ? '—' : (units.data ?? []).length.toLocaleString()}</strong>
        </div>
        <div>
          <span>Mapped sites</span>
          <strong>{coordinateCount.toLocaleString()}</strong>
        </div>
      </section>

      {unitsUnavailable ? (
        <div className="warning-callout" role="status">
          Units could not be loaded, so sites show no unit details. Customers, sites and their
          contacts are unaffected.{' '}
          <button type="button" className="link-button" onClick={() => void units.refetch()}>
            Retry units
          </button>
        </div>
      ) : null}

      <div className="workspace-toolbar">
        <label className="search-field">
          <span className="visually-hidden">Search customers and sites</span>
          <input
            type="search"
            value={query}
            onChange={(event) => {
              const next = new URLSearchParams(params);
              if (event.target.value) next.set('q', event.target.value);
              else next.delete('q');
              next.delete('page');
              setParams(next);
            }}
            placeholder="Search customer, site, region, official ID, contact, or address"
          />
        </label>
        <span>{groupedCustomers.length.toLocaleString()} customer groups</span>
      </div>

      {groupedCustomers.length === 0 ? (
        <div className="empty-state">
          <strong>No customers or sites match this search.</strong>
          <button type="button" onClick={() => setParams(new URLSearchParams())}>
            Clear search
          </button>
        </div>
      ) : (
        <div className="customer-groups">
          {visibleCustomers.map(({ customer, sites: customerSites }, index) => {
            const assigned = customerSites.reduce(
              (sum, site) => sum + (assignedCounts.get(site.id) ?? 0),
              0,
            );
            const located = customerSites.reduce(
              (sum, site) => sum + (locatedCounts.get(site.id) ?? 0),
              0,
            );
            return (
              <details
                className="customer-group"
                key={customer.id}
                open={Boolean(query) || index === 0}
              >
                <summary>
                  <div>
                    <strong>{customer.displayName ?? customer.name}</strong>
                    <span className="mono summary-id">{customer.id}</span>
                  </div>
                  <div className="customer-summary-counts">
                    <span>{customerSites.length} sites</span>
                    <span>{assigned.toLocaleString()} assigned</span>
                    <span>{located.toLocaleString()} located</span>
                  </div>
                </summary>
                <div className="site-card-grid">
                  {customerSites.map((site) => {
                    const siteUnits = unitsBySite.get(site.id) ?? [];
                    const coordinatesAvailable = hasCoordinates(site);
                    return (
                      <article className="site-card" key={site.id}>
                        <div className="site-card-heading">
                          <div>
                            <h2>
                              <Link
                                to={`/customers/${encodeURIComponent(site.customerId)}/sites/${encodeURIComponent(site.id)}`}
                              >
                                {siteLabel(site)}
                              </Link>
                            </h2>
                            <span className="mono">{site.id}</span>
                          </div>
                          <span
                            className={`state-badge${coordinatesAvailable ? ' state-badge-ready' : ''}`}
                          >
                            {coordinatesAvailable ? 'Map ready' : 'No coordinates'}
                          </span>
                        </div>

                        <dl className="site-details">
                          <dt>Region</dt>
                          <dd>{site.region ?? 'Not recorded'}</dd>
                          <dt>Official site ID</dt>
                          <dd className="mono">{site.officialSiteId ?? 'Not recorded'}</dd>
                          <dt>Assigned items</dt>
                          <dd>
                            <Link to={`/inventory?assignedSiteId=${encodeURIComponent(site.id)}`}>
                              {(assignedCounts.get(site.id) ?? 0).toLocaleString()} assigned here
                            </Link>
                          </dd>
                          <dt>Physically located</dt>
                          <dd>
                            <Link to={`/inventory?locationSiteId=${encodeURIComponent(site.id)}`}>
                              {(locatedCounts.get(site.id) ?? 0).toLocaleString()} physically here
                            </Link>
                          </dd>
                          <dt>Open requests</dt>
                          <dd>
                            <Link
                              to={`/customers/${encodeURIComponent(site.customerId)}/sites/${encodeURIComponent(site.id)}`}
                            >
                              {(openRequestsBySite.get(site.id) ?? 0).toLocaleString()} open
                            </Link>
                          </dd>
                        </dl>

                        <SiteContactSummary site={site} />

                        <div className="site-map-status">
                          {coordinatesAvailable ? (
                            <>
                              <span className="map-marker" aria-hidden="true">
                                ●
                              </span>
                              <span className="mono">
                                {site.latitude?.toFixed(4)}, {site.longitude?.toFixed(4)}
                              </span>
                              <a
                                href={`https://www.openstreetmap.org/?mlat=${site.latitude}&mlon=${site.longitude}#map=12/${site.latitude}/${site.longitude}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Open map
                              </a>
                            </>
                          ) : (
                            <span>Map unavailable.</span>
                          )}
                        </div>

                        <div className="unit-list">
                          <strong className="unit-count-label">Units ({siteUnits.length})</strong>
                          {siteUnits.length > 0 ? (
                            <ul>
                              {siteUnits.map((unit) => (
                                <li key={unit.id}>
                                  <span>{unit.displayName ?? unit.id}</span>
                                  <span className="state-badge">
                                    {formatUnitStatus(unit.status)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="empty-note">
                              {unitsUnavailable
                                ? 'Units could not be loaded.'
                                : 'No units recorded for this site.'}
                            </p>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </details>
            );
          })}
        </div>
      )}
      {groupedCustomers.length > 0 ? (
        <PageControls
          page={page}
          pageSize={CUSTOMER_PAGE_SIZE}
          total={groupedCustomers.length}
          onPageChange={setPage}
        />
      ) : null}
    </>
  );
}
